import { useMemo, useRef, useState, useEffect } from 'react';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { type BatchProgress, getPendingKnowledgeCleanupCount } from '@/lib/uploadProgressManager';
import { hasActiveUploads } from '@/lib/uploadOrchestrator';
import { useMatterLabels } from '@/lib/useMatterLabels';

/**
 * Hook to track document import progress
 * Subscribes to BackgroundSyncContext for reactive updates
 */
export const useUploadProgress = (matterGuid?: string) => {
  const { getState, subscribe } = useBackgroundSync();
  const [updateCount, setUpdateCount] = useState(0);
  const label = useMatterLabels();
  const labelRef = useRef(label);
  labelRef.current = label;

  useEffect(() => {
    const unsubscribe = subscribe('file-upload-progress', () => {
      setUpdateCount(prev => prev + 1);
    });
    return unsubscribe;
  }, [subscribe]);

  // Warn before closing tab if THIS TAB is running uploads
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads()) {
        const message = labelRef.current('upload-in-progress-warning');
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const filteredBatches = useMemo(() => {
    const batches = getState<BatchProgress[]>('file-upload-progress') || [];

    // If a matter GUID is provided, filter batches to only show those for this matter
    if (matterGuid) {
      return batches.filter(batch => batch.matterGuid === matterGuid);
    }

    return batches;
  }, [getState, updateCount, matterGuid]);

  const progress = useMemo(() => {
    if (filteredBatches.length === 0) return null;

    const totalFiles = filteredBatches.reduce((sum, batch) => sum + batch.totalFiles, 0);
    const completedFiles = filteredBatches.reduce((sum, batch) => sum + batch.completedFiles, 0);
    const failedFiles = filteredBatches.reduce((sum, batch) => sum + batch.failedFiles, 0);
    const isProcessing = filteredBatches.some(
      b => b.status === 'processing' || b.status === 'uploading'
    );

    return {
      totalFiles,
      completedFiles,
      failedFiles,
      progress: totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0,
      isProcessing,
    };
  }, [filteredBatches]);

  const pendingKnowledgeCleanupCount = getPendingKnowledgeCleanupCount(matterGuid);

  return {
    activeBatches: filteredBatches,
    pendingKnowledgeCleanupCount,
    progress,
    isUploading: filteredBatches.length > 0,
  };
};
